import AgoraRTM from "agora-rtm-sdk";
import EventEmitter from "events";
import * as _ from "lodash";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";

export const APP_ID = process.env.REACT_APP_AGORA_APP_ID as string;
const ENABLE_LOG = process.env.REACT_APP_AGORA_LOG === "true";
const logFilter = ENABLE_LOG
  ? AgoraRTM.LOG_FILTER_DEBUG
  : AgoraRTM.LOG_FILTER_OFF;

export enum ChatCmdType {
  chat = 1,
  muteAudio = 101,
  unmuteAudio = 102,
  muteVideo = 103,
  unmuteVideo = 104,
  pinid = 105,
  isSilent = 106,
  remoteUsersRemove = 107,
}

export interface ChannelBodyParams {
  account: string;
  recordId: string;
  content: string;
}

export interface ChannelParams {
  type: number;
  msgData: any;
}

export interface NotifyMessageParams {
  cmd: ChatCmdType;
  data: ChatMessage | UserMessage;
  enableHistoricalMessaging?: boolean;
}

export type ChatMessage = {
  account: string;
  content: string;
  userId?: string;
};

export interface UserMessage {
  uid: string;
  account: string;
  resource: string;
  value: number;
}

export interface ChatBody {
  account: string;
  content: string;
}

export interface PeerMessage {
  uid: string;
  userId: string;
  account: string;
  operate: number;
}

export interface MessageBody {
  cmd?: ChatCmdType;
  text?: string;
  data?: ChatMessage | UserMessage | PeerMessage;
  type?: number;
  msgData?: any;
}

export default class AgoraRTMClient {
  private _bus: EventEmitter;
  public _currentChannel: any;
  public _currentChannelName: string | any;
  private _channels: any;
  public _client: any;
  private _channelAttrsKey: string | any;
  public _logged: boolean = false;
  private _joined: boolean = false;
  private _eventRoomStore: any;
  // Real RTM connection state — distinct from _logged, which only reflects that
  // login() resolved. The SDK can be _logged === true while the underlying
  // connection is RECONNECTING/DISCONNECTED, in which case publish() fails with
  // -10025 and the message is silently lost (local-echo masks it for the sender).
  // Sends are guarded on this state and queued while not CONNECTED.
  public _connectionState: string = "DISCONNECTED";
  private _pendingSends: Array<() => Promise<any>> = [];
  private _flushing: boolean = false;
  // Live occupant list sourced from the pushed "presence" events (SNAPSHOT +
  // JOIN/LEAVE deltas). Replaces the getOnlineUsers PULL, whose whoNow path throws
  // an uncaught SDK ReferenceError that poisons RTM until a page refresh
  // (host incidents 2026-06-20 / -25 / -28: mic shown open but inaudible, chat KO).
  private _presenceOccupants: Set<string> = new Set();

  constructor(eventRoomStore?: any) {
    this._bus = new EventEmitter();
    this._channels = {};
    this._currentChannel = null;
    this._currentChannelName = null;
    this._channelAttrsKey = null;
    this._client = null;
    this._eventRoomStore = eventRoomStore;
  }

  public removeAllListeners(): any {
    this._bus.removeAllListeners();
  }

  destroy(): void {
    for (let channel of Object.keys(this._channels)) {
      if (this._channels[channel]) {
        this._channels[channel].removeAllListeners();
        this._channels[channel] = null;
      }
    }
    this._currentChannel = null;
    this._currentChannelName = null;
    this._client.removeAllListeners();
  }

  on(evtName: string, cb: (args: any) => void) {
    this._bus.on(evtName, cb);
  }

  off(evtName: string, cb: (args: any) => void) {
    this._bus.off(evtName, cb);
  }

  async login(appID: string, uid: string, token?: string) {
    const { RTM } = AgoraRTM;
    const appId = appID;
    const userId = uid;
    const rtm = new RTM(appId, userId);
    try {
      const result = await rtm.login({token});
      this._client = rtm;
      this._logged = true;
      // login() resolved => SDK is connected. The status listener below keeps
      // this fresh on reconnect/disconnect.
      this._connectionState = "CONNECTED";
      console.log(this._client, this._logged, "rtm-status-login");
      this._client.addEventListener("message", (event: any) => {
        console.log(event, "rtm-status-console");
        let chatMessage = JSON.parse(event.message);
        console.log("hello chatMessage", chatMessage);
        if (chatMessage.cmd === ChatCmdType.chat) {
          this._eventRoomStore._state.isMessageSent = true;
          console.log("console inside chat messaage");
          if (chatMessage?.data.type == "Private") {
            this._eventRoomStore.updatePrivateChatMessage(chatMessage.data);
            toast.success("New Private Message Received!", {
              position: "bottom-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              // Dedup if the same RTM message is processed twice (v1 bus + v2
              // listener) or rapidly retried. toastId collapses identical IDs.
              toastId: `private-${chatMessage?.data?.ts ?? Date.now()}-${chatMessage?.data?.id ?? "anon"}`,
              style: {
                backgroundColor: "#DC6E65",
                color: "#FFFFFF",
              },
            });
          } else {
            // Vérifier si c'est son propre message (déjà ajouté localement lors de l'envoi)
            const isOwnMessage = chatMessage?.data?.id && 
              chatMessage.data.id.toString() === this._eventRoomStore._state.me.uid.toString();
            
            // Ne pas ajouter les propres messages reçus via RTM (évite le doublon)
            if (!isOwnMessage) {
              this._eventRoomStore.updateChatMessage(chatMessage.data);
              
              // Notification pour les nouveaux messages (sauf invités)
              if (this._eventRoomStore._state.me.info.role !== "invitee") {
                toast("Nouveau message", {
                  autoClose: 5000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true,
                  progress: undefined,
                  // Dedup identical messages (same sender + same timestamp)
                  // — prevents the pile-up that's been visible in
                  // multi-connexion sessions where the cursor sits near
                  // the bottom-right toast zone.
                  toastId: `chat-${chatMessage?.data?.ts ?? Date.now()}-${chatMessage?.data?.id ?? "anon"}`,
                  style: {
                    backgroundColor: "#DC6E65",
                    color: "#FFFFFF",
                  },
                });
              }
            }
          }
        }
        // Receive-side hard stop: a guide/host/admin never applies a mute,
        // camera cut or removal aimed at itself by someone else, even if the
        // sender mis-resolved this stream's role (identity mismatch) — the UI
        // guards on the send side are not enough on their own. Own mic/cam
        // toggles are broadcast with our own streamId (local-*-control) but RTM
        // 2.x does not echo channel messages to their publisher; the publisher
        // check keeps that path safe regardless.
        const moderationCmds = [
          ChatCmdType.muteAudio,
          ChatCmdType.unmuteAudio,
          ChatCmdType.muteVideo,
          ChatCmdType.unmuteVideo,
          ChatCmdType.remoteUsersRemove,
        ];
        if (moderationCmds.includes(chatMessage.cmd)) {
          const me = this._eventRoomStore._state.me;
          const targetsMe =
            chatMessage.cmd === ChatCmdType.remoteUsersRemove ||
            String(chatMessage?.data?.streamId) === String(me.uid);
          const fromMe = String(event?.publisher) === String(me.uid);
          // The admin dashboard builds this client with its own store copy:
          // degrade to the previous behaviour rather than throw inside the SDK
          // callback if that copy ever lags behind on this method.
          const amProtected =
            typeof this._eventRoomStore.isProtectedRole === "function" &&
            this._eventRoomStore.isProtectedRole(me.info?.role);
          if (targetsMe && !fromMe && amProtected) {
            console.warn("[moderation] ignored command aimed at a protected role", {
              cmd: chatMessage.cmd,
              publisher: event?.publisher,
              myRole: me.info?.role,
            });
            Sentry.addBreadcrumb({
              category: "moderation",
              message: "ignored command aimed at a protected role",
              level: "warning",
              data: { cmd: chatMessage.cmd, publisher: event?.publisher, myRole: me.info?.role },
            });
            return;
          }
        }
        if (chatMessage.cmd == ChatCmdType.muteAudio) {
          const audioStreamID = chatMessage?.data?.streamId;
          this._eventRoomStore.mute(audioStreamID.toString(), "audio");
        }
        if (chatMessage.cmd === ChatCmdType.unmuteAudio) {
          const audioStreamID = chatMessage?.data?.streamId;
          this._eventRoomStore.unmute(
            audioStreamID.toString(),
            "audio"
          );
        }
        if (chatMessage.cmd === ChatCmdType.muteVideo) {
          const videoStreamID = chatMessage?.data?.streamId;
          this._eventRoomStore.mute(videoStreamID.toString(), "video");
          // This is needed to understand if user turnedOff camera or the network of the stream gone.
          this._eventRoomStore.mute(videoStreamID.toString(), "videoTurnedOn");
        }
        if (chatMessage.cmd === ChatCmdType.unmuteVideo) {
          const videoStreamID = chatMessage?.data?.streamId;
          this._eventRoomStore.unmute(
            videoStreamID.toString(),
            "video"
          );
          // This is needed to understand if user turnedOff camera or the network of the stream gone.
          this._eventRoomStore.unmute(videoStreamID.toString(), "videoTurnedOn");
        }
        if (chatMessage.cmd === ChatCmdType.isSilent) {
          console.log("Is Silent Feature Received");
        }
        if (chatMessage.cmd === ChatCmdType.remoteUsersRemove) {
          console.log("hello remove", chatMessage, event);
          // if(eventRoomStore._state.me.info.uid == chatMessage.data){
          sessionStorage.removeItem("visitStarted");
          window.location.href = "/end";
          // }
        }
        if (chatMessage.cmd === ChatCmdType.pinid) {
          this._eventRoomStore.setPinId(chatMessage.data.streamID);
        }
      });
      this._client.addEventListener("presence", (event: any) => {
        // Update the occupant cache from the event itself (no getOnlineUsers pull),
        // then refresh usersAsAudience via the now cache-backed members list.
        this._applyPresenceEvent(event);
        this._eventRoomStore.getMembersList(this._eventRoomStore.state.me.channelName);
      });
      // Topic
      this._client.addEventListener("topic", (event: any) => {
        console.log(event, "rtm-status-topic");
        const action = event.evenType; // The action. Should be one of 'SNAPSHOT'、'JOIN'、'LEAVE'.
        const channelName = event.channelName; // The channel this event came from
        const publisher = event.userId; // Who triggered this event
        const topicInfos = event.topicInfos; // Topic information payload
        const totalTopics = event.totalTopics; // How many topics
      });
      // Storage
      this._client.addEventListener("storage", (event: any) => {
        console.log(event, "rtm-status-storage");
        const channelType = event.channelType; // The channel type. Should be "STREAM" or "MESSAGE" .
        const channelName = event.channelName; // The channel this event came from
        const publisher = event.publisher; // Who triggered this event
        const storageType = event.storageType; // Which category the event is, should be 'USER'、'CHANNEL'
        const action = event.eventType; // The action. Should be one of "SNAPSHOT"、"SET"、"REMOVE"、"UPDATE" or "NONE"
        const data = event.data; // 'USER_METADATA' or 'CHANNEL_METADATA' payload
      });
      // Lock
      this._client.addEventListener("lock", (event: any) => {
        console.log(event, "rtm-status-lock");
        const channelType = event.channelType; // The channel type. Should be "STREAM" or "MESSAGE" .
        const channelName = event.channelName; // The channel this event came from
        const publisher = event.publisher; // Who triggered this event
        const action = event.evenType; // The action. Should be one of 'SET'、'REMOVED'、'ACQUIRED'、'RELEASED'、'EXPIRED'、'SNAPSHOT'
        const lockName = event.lockName; // Which lock it affected
        const ttl = event.ttl; // The ttl of this lock
        const snapshot = event.snapshot; // Snapshot payload
      });
      // Connection State Change
      this._client.addEventListener("status", (event: any) => {
        const prev = this._connectionState;
        this._connectionState = event?.state || this._connectionState;
        console.log("[RTM] connection status", prev, "->", this._connectionState, "reason:", event?.reason);
        // Reconnected: flush sends that were queued while offline so a residence's
        // messages aren't silently lost on a flaky connection.
        if (this._connectionState === "CONNECTED") {
          this._flushPendingSends();
        }
      });
    } catch (err) {
      console.error("[RTM] login failed", { uid, err });
      Sentry.captureException(err, {
        tags: { component: "agora-rtm", op: "login" },
        extra: { uid },
      });
      this._client?.removeAllListeners?.();
      throw err;
    }
  }

  async logout() {
    if (!this._logged) return;
    await this._client.logout();
    this.destroy();
    this._logged = false;
    this._connectionState = "DISCONNECTED";
    this._pendingSends = [];
    this._presenceOccupants.clear();
    return;
  }

  setUserExtraAttributes(userName: any) {
    this._client.addOrUpdateLocalUserAttributes({ name: userName });
  }

  getUserExtraAttributes(userId: any) {
    //return this._client.getUserAttributes(userId);
    return this._client.getUserAttributesByKeys(userId, ["name"]);
  }

  async join(channel: string) {
    const subscribeOptions = {
      withMessage: true,
      withPresence: true,
      withMetadata: false,  // Désactivé : Storage service non disponible
      withLock: false,      // Désactivé : Storage service non disponible
    };
    const inboxUid = this._eventRoomStore?._state?.me?.info?.uid;
    try {
      await this._client.subscribe(channel, subscribeOptions);
      if (inboxUid) {
        await this._client.subscribe(`inbox_${inboxUid}`, subscribeOptions);
      } else {
        console.warn("[RTM] inbox subscribe skipped: me.info.uid is missing");
      }
      this._joined = true;
    } catch (error) {
      console.error("[RTM] subscribe failed", { channel, inboxUid, error });
      Sentry.captureException(error, {
        tags: { component: "agora-rtm", op: "subscribe" },
        extra: { channel, inboxUid },
      });
      this._joined = false;
      throw error;
    }
    return;
  }

  destroyChannel(channel: string) {
    if (this._channels[channel]) {
      this._channels[channel].removeAllListeners();
      this._channels[channel] = null;
    }
  }

  async leave(channel: string) {
    if (this._channels[channel]) {
      // await this._channels[channel].leave();
      this._joined = false;
      this.destroyChannel(channel);
    }
  }

  async exit() {
    try {
      await this.deleteChannelAttributesByKey();
    } catch (err) {
    } finally {
      await this.leave(this._currentChannelName);
      await this.logout();
    }
  }

  // Apply a pushed "presence" event to the occupant cache. Pure-ish + public for
  // tests. SDK eventType ∈ SNAPSHOT/INTERVAL/(REMOTE_)JOIN/(REMOTE_)LEAVE/
  // (REMOTE_)TIMEOUT/STATE_CHANGED/OUT_OF_SERVICE.
  _applyPresenceEvent(event: any): void {
    const action = event?.eventType;
    const publisher = event?.publisher;
    const snapshot = event?.snapshot;
    if (action === "SNAPSHOT") {
      // Full authoritative occupant list (UserState[]) → reset the cache.
      this._presenceOccupants = new Set(
        (Array.isArray(snapshot) ? snapshot : [])
          .map((u: any) => (typeof u === "string" ? u : u?.userId))
          .filter(Boolean)
      );
    } else if (action === "INTERVAL") {
      // Interval mode (large channels) sends BATCHED DELTAS in event.interval —
      // NOT a snapshot (snapshot is null here). Apply them; do NOT reset, or every
      // interval would wipe the stable occupants who didn't change. UserList.users
      // is string[] of userIds (agora-rtm 2.2.4 d.ts).
      const iv = event?.interval || {};
      (iv.join?.users || []).forEach((u: string) => this._presenceOccupants.add(u));
      (iv.leave?.users || []).forEach((u: string) => this._presenceOccupants.delete(u));
      (iv.timeout?.users || []).forEach((u: string) => this._presenceOccupants.delete(u));
    } else if (action === "JOIN" || action === "REMOTE_JOIN") {
      if (publisher) this._presenceOccupants.add(publisher);
    } else if (
      action === "LEAVE" || action === "REMOTE_LEAVE" ||
      action === "TIMEOUT" || action === "REMOTE_TIMEOUT" ||
      action === "OUT_OF_SERVICE"
    ) {
      if (publisher) this._presenceOccupants.delete(publisher);
    }
    console.log("[RTM] presence", action, "→ occupants:", this._presenceOccupants.size);
  }

  // Cache-backed (no getOnlineUsers pull). The pull's whoNow path throws an
  // uncaught SDK ReferenceError that poisons RTM until refresh — see field doc.
  async getChannelMembersList(_channel: string): Promise<string[]> {
    return Array.from(this._presenceOccupants);
  }

  // RTM timeout while CONNECTED (slow network) — worth one retry, unlike
  // genuine publish errors. -11026 = RtmTimeoutError "Publish message timeout".
  private _isTimeoutError(error: any): boolean {
    return (
      error?.code === -11026 ||
      /timeout/i.test(error?.name || "") ||
      /timeout/i.test(error?.message || "")
    );
  }

  private _enqueueSend(thunk: () => Promise<any>, meta: any) {
    const MAX_PENDING_SENDS = 50; // bound the offline queue against a long outage
    if (this._pendingSends.length >= MAX_PENDING_SENDS) {
      this._pendingSends.shift(); // drop oldest to stay bounded
      console.warn("[RTM] pending-send queue full, dropped oldest");
    }
    this._pendingSends.push(thunk);
    console.warn("[RTM] send queued — not connected", { state: this._connectionState, queued: this._pendingSends.length, ...meta });
    Sentry.addBreadcrumb({
      category: "agora-rtm",
      message: "send queued (not connected)",
      data: { state: this._connectionState, queued: this._pendingSends.length, ...meta },
      level: "info",
    });
  }

  private async _flushPendingSends(): Promise<void> {
    if (this._flushing) return; // single-flight: never drain concurrently
    this._flushing = true;
    try {
      // Process from the front; only remove a message once it's actually sent, and
      // re-check the connection on every iteration so a mid-flush drop (bug 2) stops
      // the drain with the remaining messages still queued — in order — for retry.
      while (this._pendingSends.length && this._connectionState === "CONNECTED") {
        const thunk = this._pendingSends[0];
        try {
          await thunk();
          this._pendingSends.shift(); // delivered → drop from front (FIFO preserved)
        } catch (err) {
          if (this._connectionState !== "CONNECTED") {
            // connection fell during the await → keep this one + the rest queued,
            // retry on next reconnect. No message lost.
            console.warn("[RTM] flush interrupted by disconnect; keeping messages queued");
            break;
          }
          // failed while still CONNECTED → genuine publish error; drop to avoid an
          // infinite retry loop (already captured to Sentry in the publish helper).
          this._pendingSends.shift();
          console.error("[RTM] queued send dropped after publish error", err);
        }
      }
    } finally {
      this._flushing = false;
    }
    // A send may have been enqueued in the gap between the last length check and
    // clearing _flushing — drain again so it isn't stranded until the next reconnect.
    if (this._pendingSends.length && this._connectionState === "CONNECTED") {
      return this._flushPendingSends();
    }
  }

  async notifyMessage(params: NotifyMessageParams, chanelName: any) {
    const { cmd } = params;
    if (!this._client || !this._logged) {
      console.warn("[RTM] notifyMessage skipped: client not ready", { chanelName, cmd, logged: this._logged });
      return;
    }
    // Lightweight breadcrumb so a future incident shows the send attempt + the
    // connection state at that moment (cmd 101/102 = mute/unmute audio). The app
    // doesn't capture ui.click, so this is how we'll prove click↔failure next time.
    Sentry.addBreadcrumb({
      category: "agora-rtm",
      message: "channel send",
      data: { cmd, state: this._connectionState },
      level: "info",
    });
    const publishMessage = JSON.stringify(params);
    const doPublish = async (retried = false): Promise<any> => {
      try {
        return await this._client.publish(chanelName, publishMessage);
      } catch (error: any) {
        console.error("[RTM] publish channel message failed", { chanelName, cmd, error, retried });
        Sentry.captureException(error, {
          tags: { component: "agora-rtm", op: "publish-channel" },
          extra: { chanelName, cmd, errorCode: error?.code, retried },
        });
        // -11026 = publish TIMEOUT while CONNECTED (slow network): the message —
        // possibly a moderation mute (cmd 101, TM-FRONTEND-2P) — would be silently
        // lost. Re-enqueue ONCE via the existing offline queue; the flush loop's
        // drop-on-second-failure prevents retry storms.
        if (!retried && this._isTimeoutError(error)) {
          console.warn("[RTM] publish timeout — re-queueing once", { cmd });
          this._enqueueSend(() => doPublish(true), { op: "publish-channel", cmd, retried: true });
          void this._flushPendingSends();
          return;
        }
        throw error;
      }
    };
    // Guard on REAL connection state, not just _logged. While not CONNECTED
    // (e.g. RECONNECTING after a network blip), queue the send and flush on
    // reconnect rather than firing into a disconnected client (-10025 = silent
    // message loss). The sender already saw their local echo, so UX is preserved.
    // Queue when not CONNECTED, OR while a flush is draining — so a fresh send
    // can't jump ahead of the messages already waiting (FIFO, bug 3).
    if (this._connectionState !== "CONNECTED" || this._flushing) {
      this._enqueueSend(doPublish, { op: "publish-channel", cmd });
      return;
    }
    return await doPublish();
  }

  async sendPeerMessage(peerId: string, body: MessageBody, channelName: any) {
    if (!this._client || !this._logged) {
      console.warn("[RTM] sendPeerMessage skipped: client not ready", { peerId, cmd: body.type, logged: this._logged });
      return;
    }
    Sentry.addBreadcrumb({
      category: "agora-rtm",
      message: "peer send",
      data: { cmd: body.type, state: this._connectionState },
      level: "info",
    });
    const payload = {
      cmd: body.type,
      data: body?.msgData,
      enableHistoricalMessaging: true,
    };
    const doPublish = async (retried = false): Promise<any> => {
      try {
        const finaldata = JSON.stringify(payload);
        const sendingId = `inbox_${peerId}`;
        const result = await this._client.publish(sendingId, finaldata);
        console.log(result);
        return result;
      } catch (error: any) {
        console.error("[RTM] publish peer message failed", { peerId, channelName, cmd: body.type, error, retried });
        Sentry.captureException(error, {
          tags: { component: "agora-rtm", op: "publish-peer" },
          extra: { peerId, channelName, cmd: body.type, errorCode: error?.code, retried },
        });
        // Same one-shot timeout retry as notifyMessage (see -11026 note there).
        if (!retried && this._isTimeoutError(error)) {
          console.warn("[RTM] peer publish timeout — re-queueing once", { cmd: body.type });
          this._enqueueSend(() => doPublish(true), { op: "publish-peer", cmd: body.type, retried: true });
          void this._flushPendingSends();
          return;
        }
        throw error;
      }
    };
    // Same connection guard as notifyMessage: queue while not CONNECTED, flush on
    // reconnect, instead of losing the peer message to -10025.
    if (this._connectionState !== "CONNECTED" || this._flushing) {
      this._enqueueSend(doPublish, { op: "publish-peer", cmd: body.type });
      return;
    }
    return await doPublish();
  }

  async sendChannelMessage(data: ChannelParams, chanelName: any) {
    return this.notifyMessage(
      {
        cmd: data.type,
        data: data.msgData,
        enableHistoricalMessaging: true,
      },
      chanelName
    );
  }

  async updateChannelAttrsByKey(key: string, attrs: any) {
    this._channelAttrsKey = key;
    const channelAttributes: { [key: string]: string } = {};
    if (key) {
      channelAttributes[key] = JSON.stringify(attrs);
    }
    await this._client.addOrUpdateChannelAttributes(
      this._currentChannelName,
      channelAttributes,
      { enableNotificationToChannelMembers: true }
    );
  }

  async deleteChannelAttributesByKey() {
    if (!this._channelAttrsKey) return;
    await this._client.deleteChannelAttributesByKeys(
      this._currentChannelName,
      [this._channelAttrsKey],
      { enableNotificationToChannelMembers: true }
    );
    this._channelAttrsKey = null;
    return;
  }

  async getChannelAttrs(): Promise<string> {
    let json = await this._client.getChannelAttributes(
      this._currentChannelName
    );
    return JSON.stringify(json);
  }

  // Cache-backed (no getOnlineUsers pull) — viewer count = occupant cache size.
  async getChannelMemberCount(_ids: string[]) {
    return this._presenceOccupants.size;
  }
}